import { useId, useRef } from 'react'
import { Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AiChatDropRegion } from './files/ai-chat-composer-files'
import type { KeyboardEvent, ReactNode } from 'react'
import { Button } from '@/shared/ui/button'
import { Textarea } from '@/shared/ui/textarea'

function isPlainEnter(event: KeyboardEvent<HTMLTextAreaElement>) {
  return (
    event.key === 'Enter' &&
    !event.shiftKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey
  )
}

type ChatComposerProps = {
  inputDisabled: boolean
  sendBlocked: boolean
  draft: string
  onDraftChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  notice?: string
  footerAction?: ReactNode
  /** Files waiting to be sent; rendered above the footer. */
  fileTray?: ReactNode
  attachControl?: ReactNode
  /** At least one uploaded file is ready, so an empty message may be sent. */
  filesReady?: boolean
  /** A file is still uploading or failed; sending waits until resolved. */
  filesBlocked?: boolean
  onFilesDropped?: (files: Array<File>) => void
}

/** Enter sends unless a modifier, key repeat or IME composition is active. */
function useEnterToSend(disabled: boolean) {
  const composingRef = useRef(false)
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      !isPlainEnter(event) ||
      composingRef.current ||
      event.nativeEvent.isComposing ||
      event.nativeEvent.keyCode === 229
    ) {
      return
    }
    event.preventDefault()
    if (!event.repeat && !disabled) event.currentTarget.form?.requestSubmit()
  }

  return { composingRef, onKeyDown }
}

export function ChatComposer({
  filesReady = false,
  filesBlocked = false,
  onFilesDropped,
  ...props
}: ChatComposerProps) {
  const disabled =
    props.inputDisabled ||
    props.sendBlocked ||
    filesBlocked ||
    (!props.draft.trim() && !filesReady)
  const form = <ComposerForm {...props} disabled={disabled} />

  return onFilesDropped ? (
    <AiChatDropRegion
      disabled={props.inputDisabled || props.sendBlocked}
      onFiles={onFilesDropped}
    >
      {form}
    </AiChatDropRegion>
  ) : (
    form
  )
}

function ComposerForm({
  inputDisabled,
  draft,
  onDraftChange,
  onSubmit,
  placeholder,
  notice,
  footerAction,
  fileTray,
  attachControl,
  disabled,
}: Omit<ChatComposerProps, 'filesReady' | 'filesBlocked' | 'onFilesDropped'> & {
  disabled: boolean
}) {
  const { t } = useTranslation()
  const id = useId()
  const { composingRef, onKeyDown } = useEnterToSend(disabled)
  const helperText = `${t('aiChat:keyboardHint')}\n${notice ?? t('aiChat:writeNotice')}`

  return (
    <form
      className="shrink-0 border-t bg-background p-5"
      onSubmit={(event) => {
        event.preventDefault()
        if (!disabled && !composingRef.current) onSubmit()
      }}
    >
      <label htmlFor={id} className="text-sm font-medium">
        Message
      </label>
      <Textarea
        id={id}
        value={draft}
        disabled={inputDisabled}
        aria-description={helperText.replace('\n', ' ')}
        placeholder={placeholder ?? t('aiChat:messagePlaceholder')}
        className="mt-2 min-h-24 max-h-48 resize-none overflow-y-auto"
        onChange={(event) => onDraftChange(event.target.value)}
        onCompositionStart={() => {
          composingRef.current = true
        }}
        onCompositionEnd={() => {
          composingRef.current = false
        }}
        onBlur={() => {
          composingRef.current = false
        }}
        onKeyDown={onKeyDown}
      />
      {fileTray}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {attachControl}
          {footerAction}
        </div>
        <Button type="submit" disabled={disabled} title={helperText}>
          <Send className="size-4" aria-hidden="true" />
          {t('aiChat:send')}
        </Button>
      </div>
    </form>
  )
}
